import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { Button } from "@/src/components/ui/button"
import { Alert, AlertDescription } from '@/src/components/ui/alert'
import { useState, useEffect } from "react"
import { SpinnerOverlay } from "@/src/components/SpinnerOverlay"
import { useAuth } from "@/src/components/AuthContextProvider"
import { authClient, passkey } from "@/src/lib/auth-client"
import { useTranslation } from "@/src/i18n/client"
import { z } from 'zod'

const loginSearchSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
  callbackUrl: z.string().optional(),
})

export const Route = createFileRoute('/_auth/login/')({
  component: LoginPage,
  validateSearch: loginSearchSchema,
})

function getHumanReadableError(error?: string, description?: string): string | null {
  if (!error && !description) return null
  
  // Map common OAuth error codes to human-readable messages
  const errorMessages: Record<string, string> = {
    'auth_cancelled': 'Authentication was cancelled',
    'access_denied': 'Access was denied',
    'server_error': 'Server error occurred during authentication',
    'temporarily_unavailable': 'Authentication service is temporarily unavailable',
    'invalid_request': 'Invalid authentication request',
    'unauthorized_client': 'Unauthorized client',
  }
  
  if (error && errorMessages[error]) {
    return description ? `${errorMessages[error]}: ${description}` : errorMessages[error]
  }
  
  return description || error || 'Authentication error occurred'
}

function LoginPage() {
  const { t } = useTranslation('auth')
  const search = useSearch({ from: '/_auth/login/' })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { status, signIn } = useAuth()
  
  // Get error from URL params
  const urlError = getHumanReadableError(search.error, search.error_description)

  useEffect(() => {
    if (status === "authenticated") {
      // Check for callbackUrl in search params
      const callbackUrl = search.callbackUrl || "/"
      window.location.assign(callbackUrl)
    }
  }, [status, search.callbackUrl])

  const handlePasskeySignIn = async () => {
    setError(null)
    setIsLoading(true)
    
    try {
      const result = await authClient.signIn.passkey()
      
      if (result.error) {
        setError(result.error.message || t('login.errorPasskey'))
      } else {
        // Force page reload to refresh auth context
        const callbackUrl = search.callbackUrl || "/"
        window.location.href = callbackUrl
      }
    } catch (err: any) {
      setError(err.message || t('login.errorPasskeySupport'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative">
      {isLoading && <SpinnerOverlay text={t('login.signingIn')} />}
      <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">{t('login.title')}</h2>
      
      {(error || urlError) && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error || urlError}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Social login buttons */}
      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          disabled={isLoading}
          onClick={async () => {
            setIsLoading(true)
            try {
              await signIn("github")
            } finally {
              setIsLoading(false)
            }
          }}
        >
          <img src="/assets/github.svg" alt="GitHub" className="w-5 h-5" />
          {t('login.continueWithGitHub')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          disabled={isLoading}
          onClick={async () => {
            setIsLoading(true)
            try {
              await signIn("google")
            } finally {
              setIsLoading(false)
            }
          }}
        >
          <img src="/assets/google.svg" alt="Google" className="w-5 h-5" />
          {t('login.continueWithGoogle')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          disabled={isLoading}
          onClick={async () => {
            setIsLoading(true)
            try {
              await signIn("atlassian")
            } finally {
              setIsLoading(false)
            }
          }}
        >
          <img src="/assets/atlassian.svg" alt="Atlassian" className="w-5 h-5" />
          {t('login.continueWithAtlassian')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          disabled={isLoading}
          onClick={handlePasskeySignIn}
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" 
            />
          </svg>
          {t('login.continueWithPasskey')}
        </Button>
      </div>

      {/* Separator */}
      <div className="my-6 flex items-center justify-center">
        <div className="grow border-t border-gray-300 dark:border-gray-600"></div>
        <span className="mx-4 shrink text-sm text-gray-500 dark:text-gray-400">{t('login.separator')}</span>
        <div className="grow border-t border-gray-300 dark:border-gray-600"></div>
      </div>

      {/* Email/Password link */}
      <Link to="/login/email">
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          disabled={isLoading}
        >
          {t('login.signInWithEmail')}
        </Button>
      </Link>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t('login.noAccount')}{' '}
        <Link to="/register" className="font-medium text-primary hover:text-primary/80">
          {t('login.createAccount')}
        </Link>
      </p>
    </div>
  )
}
